/**
 * Validation functions for GL Key Management
 */

/**
 * Validate a field value
 * @param {string} field - Field name
 * @param {any} value - Field value
 * @param {object} rowData - Complete row data
 * @param {object} context - Validation context (entities, departments, etc.)
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateField = (field, value, rowData, context = {}) => {
  // Required field validation
  const requiredFields = ['entity', 'entity_id', 'department_name', 'department_id', 'account_id', 'account_name', 'acct_type'];
  
  if (requiredFields.includes(field)) {
    if (value === null || value === undefined || String(value).trim() === '') {
      return {
        isValid: false,
        error: `${field} is required`,
      };
    }
  }

  // Field-specific validation
  switch (field) {
    case 'entity_id':
    case 'department_id':
    case 'account_id':
      // IDs must be numeric
      if (!/^\d+$/.test(String(value).trim())) {
        return {
          isValid: false,
          error: `${field} must be numeric`,
        };
      }
      break;

    case 'account_name':
      // Account name validation
      const accountName = String(value).trim();
      
      // Check for formula injection
      if (/^[\s]*[=+\-@\t\r]/.test(accountName)) {
        return {
          isValid: false,
          error: 'Account name cannot start with =, +, -, @, or special characters',
        };
      }
      
      // Check for dangerous characters
      if (/[;|\\<>]/.test(accountName)) {
        return {
          isValid: false,
          error: 'Account name contains invalid characters (;|\\<>)',
        };
      }
      
      // Length validation
      if (accountName.length > 255) {
        return {
          isValid: false,
          error: 'Account name is too long (max 255 characters)',
        };
      }
      break;

    case 'acct_type':
      // Validate account type
      const validAcctTypes = ['Revenue', 'Expense', 'Deduction', 'Other Revenue', 'Asset', 'Liability', 'Equity', 'Stat', 'P&L'];
      if (!validAcctTypes.includes(value)) {
        return {
          isValid: false,
          error: `Invalid account type. Must be one of: ${validAcctTypes.join(', ')}`,
        };
      }
      break;

    case 'fixed_or_variable':
      // Validate fixed/variable
      const validFixedVariable = ['Fixed', 'Variable', 'Stat', ''];
      if (value && !validFixedVariable.includes(value)) {
        return {
          isValid: false,
          error: 'Invalid value. Must be: Fixed, Variable, Stat, or blank',
        };
      }
      break;

    case 'driver':
      // Stat accounts cannot have drivers
      if (rowData.acct_type === 'Stat' && value && String(value).trim() !== '') {
        return {
          isValid: false,
          error: 'Stat accounts cannot have drivers',
        };
      }
      break;

    default:
      break;
  }

  return { isValid: true, error: null };
};

/**
 * Validate entire row before save
 * @param {object} rowData - Row data to validate
 * @param {object} context - Validation context
 * @returns {object} { isValid: boolean, errors: Array }
 */
export const validateRow = (rowData, context = {}) => {
  const errors = [];

  // Required fields
  const requiredFields = [
    { field: 'entity_id', label: 'Entity ID' },
    { field: 'entity', label: 'Entity' },
    { field: 'department_id', label: 'Department ID' },
    { field: 'department_name', label: 'Department' },
    { field: 'account_id', label: 'Account ID' },
    { field: 'account_name', label: 'Account Name' },
    { field: 'acct_type', label: 'Account Type' },
  ];

  requiredFields.forEach(({ field, label }) => {
    const value = rowData[field];
    if (value === null || value === undefined || String(value).trim() === '') {
      errors.push(`${label} is required`);
    }
  });

  // If required fields are missing, return early
  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  // Validate each field
  Object.keys(rowData).forEach(field => {
    const validation = validateField(field, rowData[field], rowData, context);
    if (!validation.isValid) {
      errors.push(validation.error);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get cell style based on validation and change status
 * @param {object} params - AG Grid cell params
 * @param {object} validation - Validation errors map
 * @returns {object} Style object
 */
export const getCellStyle = (params, validation = {}) => {
  const { data, colDef } = params;
  const field = colDef.field;

  // Check for validation errors
  if (validation[data.id] && validation[data.id][field]) {
    return { 
      backgroundColor: '#ffebee', 
      borderLeft: '2px solid #f44336' 
    };
  }

  // Check for changes
  if (data._changed && data._changed[field]) {
    return { backgroundColor: '#fff3cd' };
  }

  // Read-only fields
  if (!colDef.editable) {
    return { backgroundColor: '#f5f5f5' };
  }

  return null;
};

/**
 * Get row style based on row status
 * @param {object} params - AG Grid row params
 * @returns {object} Style object
 */
export const getRowStyle = (params) => {
  const { data } = params;

  // New rows (blue background)
  if (data._isNew) {
    return { backgroundColor: '#d1ecf1' };
  }

  // Rows with changes (yellow left border)
  if (data._hasChanges) {
    return { borderLeft: '3px solid #ffc107' };
  }

  return null;
};
